// Full-height brand surface. Always paints the paper bg ourselves so the
// device's default white doesn't bleed through. flex=true switches to a
// column layout so chat/composer screens can pin the composer to the
// bottom of the viewport.
//
// Renders as <main id="main-content"> by default so each screen has a
// landmark for screen readers + the skip-to-content link can target it.
// Pass `as="div"` for sub-surfaces that aren't the page's primary content.
//
// Ported from handoff_input/design/screens/shared.jsx :: ICSSurface.

import type { CSSProperties, ReactNode } from "react";
import { cn } from "@/lib/utils";

export interface SurfaceProps {
  children: ReactNode;
  /** Allow content to scroll vertically. Default true. */
  scroll?: boolean;
  /** Reserve space at the bottom (px) — used by Recommendation to leave room
   *  for the sticky CTA bar. */
  padBottom?: number;
  /** Switch to flex-column so a child marked flex-1 fills the remaining
   *  height (chat thread above, composer pinned bottom). */
  flex?: boolean;
  /** Landmark element. Defaults to "main"; pass "div" for non-primary
   *  sub-surfaces (e.g. inside a route that already has its own main). */
  as?: "main" | "div";
  className?: string;
}

export function Surface({
  children,
  scroll = true,
  padBottom = 0,
  flex = false,
  as = "main",
  className,
}: SurfaceProps) {
  const style: CSSProperties = {
    paddingBottom: padBottom > 0 ? padBottom : undefined,
  };
  const commonProps = {
    className: cn(
      "ics-noscroll relative h-full bg-paper text-ink antialiased",
      flex ? "flex flex-col overflow-hidden" : scroll ? "overflow-y-auto" : "overflow-hidden",
      className,
    ),
    style,
  };
  if (as === "main") {
    return (
      <main id="main-content" tabIndex={-1} {...commonProps}>
        {children}
      </main>
    );
  }
  return <div {...commonProps}>{children}</div>;
}
